import { createSlice } from "@reduxjs/toolkit";
import { normalizeBookingSearchDates } from "../utils/bookingSearchDates";

const normalizeMode = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "experiences" || raw === "experience") return "experiences";
  if (raw === "itinerary" || raw === "itinerary-planner") return "itinerary";
  return "stays";
};

const normalizeExperienceType = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "safari" || raw === "jungle-safari") return "safari";
  if (raw === "cruise" || raw === "cruise-boating" || raw === "boating") return "cruise";
  if (raw === "trek" || raw === "trekking" || raw === "trail") return "trek";
  return "safari";
};

const normalizeExperienceCategory = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  return raw;
};

const normalizeTripType = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "roundtrip" || raw === "round-trip" || raw === "return") return "roundTrip";
  return "oneWay";
};

const parseDateOnly = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = new Date(`${raw}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const computeTotalDaysFromDates = (dates = {}) => {
  const start = parseDateOnly(dates?.startDate);
  const end = parseDateOnly(dates?.endDate);
  if (start && end && end >= start) {
    return Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  }
  if (start || end) return 1;
  return 0;
};

const normalizeTotalDays = (value, fallback = 0) => {
  const next = Number(value);
  if (Number.isFinite(next)) {
    return Math.max(0, Math.min(30, Math.round(next)));
  }
  return Math.max(0, Math.min(30, Math.round(Number(fallback) || 0)));
};

const hasOwn = (payload, key) => Object.prototype.hasOwnProperty.call(payload || {}, key);

const normalizeLocation = (payload = {}) => {
  const district = String(payload.district || "").trim();
  const destinationSlug = String(payload.destinationSlug || "").trim();
  const destinationLabel = String(payload.destinationLabel || "").trim();
  const location = String(payload.location || "").trim();
  const campSlug = String(payload.campSlug || payload.camp || "").trim();
  const experienceTypeRaw = String(payload.experienceType || payload.type || "").trim().toLowerCase();
  const experienceType =
    experienceTypeRaw === "safari" || experienceTypeRaw === "cruise" || experienceTypeRaw === "trek"
      ? normalizeExperienceType(experienceTypeRaw)
      : "";
  const experienceSlug = String(payload.experienceSlug || payload.slug || "").trim();
  const experiencePath = String(payload.experiencePath || payload.path || payload.href || "").trim();
  const experienceCategory = String(payload.experienceCategory || payload.categorySlug || "").trim().toLowerCase();
  return {
    district,
    destinationSlug,
    destinationLabel,
    location,
    campSlug,
    experienceType,
    experienceSlug,
    experiencePath,
    experienceCategory,
  };
};

const normalizeGuests = (payload = {}) => {
  const adults = Math.max(1, Number(payload.adults ?? payload.adult ?? payload.adultCount ?? 1) || 1);
  const children = Math.max(0, Number(payload.children ?? payload.child ?? payload.childCount ?? 0) || 0);
  const infants = Math.max(0, Number(payload.infants ?? payload.infant ?? payload.infantCount ?? 0) || 0);
  return { adults, children, infants };
};

const normalizeItinerary = (payload = {}) => {
  const origin = String(payload.origin || payload.from || "").trim();
  const destination = String(payload.destination || payload.to || "").trim();
  const tripType = normalizeTripType(payload.tripType);
  const returnToRaw = String(payload.returnTo || payload.returnToInput || payload.return || "").trim();
  return {
    origin,
    destination,
    returnTo: tripType === "roundTrip" ? returnToRaw : "",
    tripType,
    totalDays: normalizeTotalDays(payload.totalDays, 0),
  };
};

const normalizeDates = (payload = {}, fallbackDates = {}, mode = "stays") => {
  const nextStart = hasOwn(payload, "startDate") ? payload.startDate : fallbackDates?.startDate;
  const nextEnd = hasOwn(payload, "endDate") ? payload.endDate : fallbackDates?.endDate;
  return normalizeBookingSearchDates({
    mode,
    startDate: nextStart,
    endDate: nextEnd,
  });
};

const initialState = {
  mode: "stays",
  experienceType: "safari",
  experienceCategory: "",
  dates: {
    startDate: "",
    endDate: "",
  },
  guests: {
    adults: 2,
    children: 0,
    infants: 0,
  },
  location: {
    district: "",
    destinationSlug: "",
    destinationLabel: "",
    location: "",
    campSlug: "",
    experienceType: "",
    experienceSlug: "",
    experiencePath: "",
    experienceCategory: "",
  },
  itinerary: {
    origin: "",
    destination: "",
    returnTo: "",
    tripType: "oneWay",
    totalDays: 0,
  },
};

const landingSearchSlice = createSlice({
  name: "landingSearch",
  initialState,
  reducers: {
    setSearchMode: (state, action) => {
      state.mode = normalizeMode(action.payload);
    },
    setSearchExperienceType: (state, action) => {
      state.experienceType = normalizeExperienceType(action.payload);
    },
    setSearchExperienceCategory: (state, action) => {
      state.experienceCategory = normalizeExperienceCategory(action.payload);
    },
    setSearchDates: (state, action) => {
      state.dates = normalizeDates(action.payload || {}, state.dates, state.mode);
      state.itinerary.totalDays = computeTotalDaysFromDates(state.dates);
    },
    setSearchGuests: (state, action) => {
      state.guests = normalizeGuests(action.payload);
    },
    setSearchLocation: (state, action) => {
      state.location = normalizeLocation(action.payload);
    },
    setSearchState: (state, action) => {
      const next = action.payload || {};
      const nextMode = normalizeMode(next.mode || state.mode);
      state.mode = nextMode;
      state.experienceType = normalizeExperienceType(next.experienceType || state.experienceType);
      const hasCategory = Object.prototype.hasOwnProperty.call(next, "experienceCategory");
      state.experienceCategory = normalizeExperienceCategory(
        hasCategory ? next.experienceCategory : state.experienceCategory,
      );
      state.dates = normalizeDates(next, state.dates, nextMode);
      state.guests = normalizeGuests(next.guests || state.guests);
      state.location = normalizeLocation(next.location || state.location);
      if (next.itinerary) {
        state.itinerary = normalizeItinerary({
          ...state.itinerary,
          ...next.itinerary,
        });
      }
      const computedTotalDays = computeTotalDaysFromDates(state.dates);
      state.itinerary = {
        ...state.itinerary,
        totalDays: normalizeTotalDays(
          next?.itinerary?.totalDays,
          computedTotalDays || state.itinerary.totalDays,
        ),
      };
    },
    setSearchItinerary: (state, action) => {
      const nextItinerary = normalizeItinerary({
        ...state.itinerary,
        ...(action.payload || {}),
      });
      state.itinerary = {
        ...nextItinerary,
        totalDays: normalizeTotalDays(
          action.payload?.totalDays,
          computeTotalDaysFromDates(state.dates) || nextItinerary.totalDays,
        ),
      };
    },
    clearSearchState: () => initialState,
  },
});

export const selectLandingSearch = (state) => state.landingSearch || initialState;
export const {
  setSearchMode,
  setSearchExperienceType,
  setSearchExperienceCategory,
  setSearchDates,
  setSearchGuests,
  setSearchLocation,
  setSearchState,
  setSearchItinerary,
  clearSearchState,
} = landingSearchSlice.actions;

export default landingSearchSlice.reducer;
